import { QUIKLEARN_V2_BACKEND_URL } from 'config/app.config';
import mockLesson720L from './mockData/lesson-720L.json';

const TOKEN_STORAGE_KEY = 'quiklearn_lcv_auth_token';

const MOCK_DATA = {
  [mockLesson720L?.metadata?.lessonId || 'G3-MATH-L013-720L']: mockLesson720L,
};

export function isMockMode() {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('useMock') === 'true';
}

export function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeToken(token) {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // silent
  }
}

/**
 * Resolve auth token: URL param takes priority, then localStorage.
 * A new param always overwrites the stored value.
 */
export function resolveToken() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const paramToken = params.get('token');
  if (paramToken) {
    storeToken(paramToken);
    return paramToken;
  }
  return getStoredToken();
}

const getBaseUrl = () => String(QUIKLEARN_V2_BACKEND_URL || '').replace(/\/$/, '');

export async function fetchLessonContent(lessonId, token) {
  if (isMockMode()) {
    const mock = MOCK_DATA[lessonId];
    if (!mock) {
      throw new Error(`No mock data for lessonId: ${lessonId}`);
    }
    console.log('[LessonContentService] Using mock data for', lessonId);
    return { content: mock };
  }

  const base = getBaseUrl();
  const url = `${base}/lesson-content/${lessonId}?includeContent=true`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const status = response.status;
    if (status === 401) {
      throw new Error('Your session has expired. Please sign in again.');
    }
    if (status === 403) {
      throw new Error('You do not have permission to view this lesson.');
    }
    if (status === 404) {
      throw new Error(`Lesson not found: ${lessonId}`);
    }
    throw new Error(`Failed to fetch lesson content (${status})`);
  }

  const json = await response.json();
  return json.data;
}

export async function fetchNextLesson(lessonId, token) {
  if (isMockMode()) {
    return {
      lessonId: 'no more lessons',
      lessonLink: null,
    };
  }

  const base = getBaseUrl();
  const url = `${base}/next-lesson-id`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ lessonId }),
  });

  if (!response.ok) {
    const status = response.status;
    if (status === 401) {
      throw new Error('Your session has expired. Please sign in again.');
    }
    if (status === 403) {
      throw new Error('You do not have permission to access the next lesson.');
    }
    if (status === 404) {
      throw new Error('Unable to find the next lesson.');
    }
    throw new Error(`Failed to fetch next lesson (${status})`);
  }

  const json = await response.json();
  return json?.data || json;
}
